/**
 * Price / quantity arithmetic — backed by decimal.js so that ticks like
 * `0.05` for an NSE F&O contract are exact in decimal space (which they are
 * not in IEEE-754 binary).
 *
 * Brands are STRING (per @/types/money) so the wire representation never
 * loses precision crossing the network. Math goes Decimal-only; we serialise
 * back to a normalised string on return.
 *
 * Note: avoid `parseFloat` on a Price/Qty — it silently truncates. Always
 * lift through `toDec()` first.
 */

import Decimal from 'decimal.js';

import { type Price, type Qty, _unsafePrice, _unsafeQty } from '@/types/money';

// decimal.js precision: 40 sig figs is far more than we need (USDT-quoted
// price * 10^18 quantities only need ~30 digits at most). Rounding to half-even
// matches NSE / SEBI conventions for cash settlements.
Decimal.set({ precision: 40, rounding: Decimal.ROUND_HALF_EVEN });

const toDec = (value: string): Decimal => new Decimal(value);

const fromDecPrice = (value: Decimal): Price => _unsafePrice(value.toFixed());
const fromDecQty = (value: Decimal): Qty => _unsafeQty(value.toFixed());

/* ============================================================================
 * Price operations
 * ============================================================================ */

export const addPrice = (a: Price, b: Price): Price => fromDecPrice(toDec(a).plus(toDec(b)));
export const subPrice = (a: Price, b: Price): Price => fromDecPrice(toDec(a).minus(toDec(b)));
export const mulPrice = (a: Price, factor: number | string): Price =>
  fromDecPrice(toDec(a).times(typeof factor === 'string' ? toDec(factor) : factor));
export const divPrice = (a: Price, divisor: number | string): Price => {
  const d = typeof divisor === 'string' ? toDec(divisor) : new Decimal(divisor);
  if (d.isZero()) {
    throw new RangeError('divPrice: division by zero');
  }
  return fromDecPrice(toDec(a).dividedBy(d));
};
export const negPrice = (a: Price): Price => fromDecPrice(toDec(a).negated());
export const absPrice = (a: Price): Price => fromDecPrice(toDec(a).abs());

/**
 * Round a price to the venue's tick size. For NSE F&O the tick is typically
 * 0.05; for NSE cash equity it's 0.05 or 0.01 depending on price band.
 */
export const roundToTick = (a: Price, tickSize: string): Price => {
  const tick = toDec(tickSize);
  if (tick.isZero()) {
    throw new RangeError('roundToTick: tick size must be > 0');
  }
  return fromDecPrice(toDec(a).dividedBy(tick).toDecimalPlaces(0, Decimal.ROUND_HALF_EVEN).times(tick));
};

/** Compare two prices. Returns -1 / 0 / +1. */
export const cmpPrice = (a: Price, b: Price): -1 | 0 | 1 => {
  const c = toDec(a).comparedTo(toDec(b));
  return (c < 0 ? -1 : c > 0 ? 1 : 0) as -1 | 0 | 1;
};

export const eqPrice = (a: Price, b: Price): boolean => cmpPrice(a, b) === 0;
export const gtPrice = (a: Price, b: Price): boolean => cmpPrice(a, b) === 1;
export const ltPrice = (a: Price, b: Price): boolean => cmpPrice(a, b) === -1;

/* ============================================================================
 * Quantity operations
 * ============================================================================ */

export const addQty = (a: Qty, b: Qty): Qty => fromDecQty(toDec(a).plus(toDec(b)));
export const subQty = (a: Qty, b: Qty): Qty => fromDecQty(toDec(a).minus(toDec(b)));
export const mulQty = (a: Qty, factor: number | string): Qty =>
  fromDecQty(toDec(a).times(typeof factor === 'string' ? toDec(factor) : factor));
export const divQty = (a: Qty, divisor: number | string): Qty => {
  const d = typeof divisor === 'string' ? toDec(divisor) : new Decimal(divisor);
  if (d.isZero()) {
    throw new RangeError('divQty: division by zero');
  }
  return fromDecQty(toDec(a).dividedBy(d));
};
export const negQty = (a: Qty): Qty => fromDecQty(toDec(a).negated());
export const absQty = (a: Qty): Qty => fromDecQty(toDec(a).abs());

export const sumQty = (values: readonly Qty[]): Qty => {
  let acc = new Decimal(0);
  for (const v of values) {
    acc = acc.plus(toDec(v));
  }
  return fromDecQty(acc);
};

export const cmpQty = (a: Qty, b: Qty): -1 | 0 | 1 => {
  const c = toDec(a).comparedTo(toDec(b));
  return (c < 0 ? -1 : c > 0 ? 1 : 0) as -1 | 0 | 1;
};

export const eqQty = (a: Qty, b: Qty): boolean => cmpQty(a, b) === 0;

/* ============================================================================
 * Cross-cutting: notional = price × qty (returned as a Price-typed string for
 * convenience; callers usually pipe this into formatINR/formatUSD which
 * accept either branded numbers or decimal strings).
 * ============================================================================ */

export const notionalPriceQty = (price: Price, qty: Qty): string =>
  toDec(price).times(toDec(qty)).toFixed();

/* ============================================================================
 * Numeric coercion at boundaries — accept either branded string or branded
 * number and produce a JS number. ONLY for chart renderers and ratio math
 * where a 15-sig-fig float is OK.
 * ============================================================================ */

export const priceToNumber = (a: Price): number => Number(a);
export const qtyToNumber = (a: Qty): number => Number(a);

/** Construct a `Price` from a JS number — for chart data we already trust. */
export const priceFromNumber = (n: number): Price => {
  if (!Number.isFinite(n)) {
    throw new RangeError(`priceFromNumber: non-finite ${n}`);
  }
  return _unsafePrice(new Decimal(n).toFixed());
};

/** Construct a `Qty` from a JS number — for chart data we already trust. */
export const qtyFromNumber = (n: number): Qty => {
  if (!Number.isFinite(n)) {
    throw new RangeError(`qtyFromNumber: non-finite ${n}`);
  }
  return _unsafeQty(new Decimal(n).toFixed());
};

/* ============================================================================
 * DISPLAY
 * ============================================================================
 *
 * How many decimals a price is shown with is a property of the INSTRUMENT, not
 * a global default. A hardcoded 2 is right for NSE cash and silently wrong for
 * anything finer: EURUSD ticks at 0.00001, so 1.15824 renders as "1.16" and the
 * whole point of an FX quote — the pip — disappears. USDJPY needs 3, XAUUSD 2,
 * a NIFTY option 2, MES 0.25 → 2.
 *
 * tick_size already carries the answer and the engine already stores it per
 * instrument, so derive from it rather than maintaining a table of special
 * cases per venue.
 */

/** Decimal places implied by a tick size. `0.00001` → 5, `0.05` → 2, `1` → 0. */
export const priceDecimals = (
  tickSize?: string | number | null,
  fallback = 2,
): number => {
  if (tickSize === null || tickSize === undefined || tickSize === '') {
    return fallback;
  }
  try {
    // Decimal, not String(): (1e-5).toString() is fine but (1e-7).toString()
    // is "1e-7", and counting characters after a "." would give 0.
    const dp = new Decimal(tickSize).decimalPlaces();
    return Number.isFinite(dp) && dp >= 0 ? Math.min(dp, 10) : fallback;
  } catch {
    return fallback;
  }
};

/**
 * Format a price for display at its instrument's own precision.
 *
 * Grouping is deliberately on (76,957.27 beats 76957.27 at a glance) but the
 * locale is PINNED to en-US: an ambient Indian locale renders 76,957 as 76,957
 * yet 7,69,570 as a lakh-grouped number, so a page showing NIFTY and EURUSD
 * side by side would group them differently.
 */
export const formatPrice = (
  value: number | null | undefined,
  tickSize?: string | number | null,
  opts: { grouping?: boolean; dash?: string } = {},
): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return opts.dash ?? '—';
  }
  const d = priceDecimals(tickSize);
  return value.toLocaleString('en-US', {
    minimumFractionDigits: d,
    maximumFractionDigits: d,
    useGrouping: opts.grouping ?? true,
  });
};
